import { useContext, useEffect, useState } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import Search from './Search'
import ParentPage from '../../../components/Tabs/ParentPage'
import { PageContext } from './context'
import MapSearch from './map/MapSearch'
import { ensureMinDuration } from '../../../helper/Request'
import useRequest from '../../../hooks/useRequest'
import { UserContext } from '../../../shared/context'
import {
	addUiBreadcrumb,
	captureFeatureException,
	captureFeatureMessage,
	getDomSnapshot,
	getNavigationSnapshot,
	setSentryFeatureContext,
} from '../../../helper/SentryMonitoring'
// import { SystemContext, UserContext } from '../../../shared/context'
// import { getDeviceFilter } from '../../../helper/Filter'

const allLinks = [
	{
		to: 'tenants',
		name: 'Standortsuche',
		comp: Search,
	},
	{
		to: 'map',
		name: 'Karte',
		comp: MapSearch,
	},
	// {
	// 	to: 'alarms',
	// 	name: 'Alarme',
	// },
]

const SearchRouter = () => {
	const { token, profile } = useContext(UserContext)
	const location = useLocation()
	const params = useParams()
	// const { systemJson } = useContext(SystemContext)
	const [tenants, setTenants] = useState(null)
	// const [devices, setDevices] = useState(null)
	const tenantId = params.tenantId || null
	const searchRoute = `${location.pathname}${location.search}`

	const { Request, RequestHeader } = useRequest()

	const fetchTenants = async () => {
		const fetchTenantsRequest = await ensureMinDuration(
			() =>
				Request(
					'/Tenant?pageSize=50000&sortBy=name&sortOrder=asc',
					RequestHeader(token),
					212100
				),
			200
		)
		return fetchTenantsRequest
	}
	/*
	const fetchDevices = async () => {
		const fetchDevicesRequest = await Request(
			'/Device',
			RequestHeader(
				token,
				'POST',
				getDeviceFilter(
					systemJson.defaultValues.TemperatureDeviceTypes,
				),
			),
			// TODO: ErrorCode
		)
		return fetchDevicesRequest
	}
	*/
	useEffect(() => {
		setSentryFeatureContext({
			feature: 'search',
			tenantId,
			path: location.pathname,
			extra: {
				route: 'search_router',
				searchRoute,
				userId: profile?.id,
				username: profile?.username,
				dom: getDomSnapshot(),
				lastNavigation: getNavigationSnapshot(),
			},
		})

		addUiBreadcrumb({
			category: 'search',
			message: 'search_router_mount',
			data: {
				currentPath: location.pathname,
				searchRoute,
				tenantId,
				userId: profile?.id,
				username: profile?.username,
				dom: getDomSnapshot(),
				lastNavigation: getNavigationSnapshot(),
			},
		})
	}, [
		location.pathname,
		location.search,
		profile?.id,
		profile?.username,
		searchRoute,
		tenantId,
	])

	useEffect(() => {
		const navigation = getNavigationSnapshot()

		if (
			navigation?.fromFeature === 'home' &&
			navigation?.toFeature === 'search'
		) {
			captureFeatureMessage('home_to_search_navigation_started', {
				feature: 'search',
				location: 'SearchRouter.navigation',
				level: 'info',
				extra: {
					currentPath: location.pathname,
					searchRoute,
					tenantId,
					userId: profile?.id,
					username: profile?.username,
					dom: getDomSnapshot(),
					lastNavigation: navigation,
					hasToken: !!token,
				},
				tags: {
					route: 'search_router',
					navigationPattern: 'home_to_search',
				},
			})
		}
	}, [
		location.pathname,
		location.search,
		profile?.id,
		profile?.username,
		searchRoute,
		tenantId,
		token,
	])

	useEffect(() => {
		const initData = async () => {
			addUiBreadcrumb({
				category: 'search',
				message: 'search_fetch_tenants_start',
				data: {
					currentPath: location.pathname,
					searchRoute,
					tenantId,
					userId: profile?.id,
					username: profile?.username,
					lastNavigation: getNavigationSnapshot(),
					dom: getDomSnapshot(),
				},
			})

			try {
				const allTenants = await fetchTenants()

				if (!allTenants?.ok) {
					const requestError = new Error(
						`Search tenant request failed with status ${allTenants?.status || 'unknown'}`
					)

					addUiBreadcrumb({
						category: 'search',
						message: 'search_fetch_tenants_error',
						level: 'error',
						data: {
							currentPath: location.pathname,
							searchRoute,
							tenantId,
							userId: profile?.id,
							username: profile?.username,
							requestStatus: allTenants?.status || null,
							lastNavigation: getNavigationSnapshot(),
						},
					})

					captureFeatureException(requestError, {
						feature: 'search',
						location: 'SearchRouter.initData',
						extra: {
							currentPath: location.pathname,
							searchRoute,
							tenantId,
							userId: profile?.id,
							username: profile?.username,
							requestStatus: allTenants?.status || null,
							responseData: allTenants?.data || null,
							dom: getDomSnapshot(),
							lastNavigation: getNavigationSnapshot(),
						},
						tags: {
							route: 'search_router',
						},
					})

					return
				}

				const allTenantsFiltered = allTenants.data.filter(
					tenant =>
						tenant?.configuration?.attributes?.customer_code !== 'APPLICATION' &&
						tenant?.configuration?.attributes?.customer_code !== 'DEMO'
				)

				addUiBreadcrumb({
					category: 'search',
					message: 'search_fetch_tenants_success',
					data: {
						currentPath: location.pathname,
						searchRoute,
						tenantId,
						fetchedTenantCount: allTenants.data.length,
						filteredTenantCount: allTenantsFiltered.length,
						lastNavigation: getNavigationSnapshot(),
					},
				})

				setTenants(allTenantsFiltered)
				// const allDevices = await fetchDevices()
				// setDevices(allDevices.data.devices)
			} catch (error) {
				addUiBreadcrumb({
					category: 'search',
					message: 'search_fetch_tenants_error',
					level: 'error',
					data: {
						currentPath: location.pathname,
						searchRoute,
						tenantId,
						userId: profile?.id,
						username: profile?.username,
						lastNavigation: getNavigationSnapshot(),
						dom: getDomSnapshot(),
					},
				})

				captureFeatureException(error, {
					feature: 'search',
					location: 'SearchRouter.initData',
					extra: {
						currentPath: location.pathname,
						searchRoute,
						tenantId,
						userId: profile?.id,
						username: profile?.username,
						dom: getDomSnapshot(),
						lastNavigation: getNavigationSnapshot(),
					},
					tags: {
						route: 'search_router',
					},
				})
			}
		}

		initData()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		location.pathname,
		location.search,
		profile?.id,
		profile?.username,
		searchRoute,
		tenantId,
	])

	return (
		<ParentPage
			allLinks={allLinks}
			PageContext={PageContext}
			contextObject={{
				tenants,
				searchDiagnostics: {
					searchRoute,
					lastNavigation: getNavigationSnapshot(),
				},
			}}
		/>
	)
}

export default SearchRouter
